import { useMutation, useQueryClient } from '@tanstack/react-query';
import { optInFeaturesQueryKeys } from '../Constants/queryKeys';
import { purgeOptInFeature } from '../Services/OptInFeatures.service';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Erase every opt-in row and every toggle event of a feature — `POST /:id/purge`, key typed back.
 *
 * **Everything the adoption screen shows counted those rows**: the six headline numbers, the daily
 * series, the reasons for leaving and the people table all come from `user_opt_ins` and
 * `user_opt_in_events`. Leaving any of them behind turns the screen into a visible contradiction —
 * an empty table beside "96 ligados agora" — which is the worst possible outcome of a destructive
 * action, because it reads as a bug in the purge rather than a stale cache.
 *
 * So adoption, users, the detail and the listing all go. Nothing is optimistic: the caller closes
 * its dialog on settle and the numbers change when the refetch brings them back.
 */
export const usePurgeOptInFeature = (featureId: string) => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: (confirmKey: string) => purgeOptInFeature(featureId, confirmKey),
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.adoption(featureId) });
      void queryClient.invalidateQueries({
        queryKey: [...optInFeaturesQueryKeys.all, 'users', featureId],
      });
      void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.detail(featureId) });
      void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.lists() });

      toast.success(
        result.deletedOptIns === 1
          ? 'Dados de 1 pessoa apagados'
          : `Dados de ${result.deletedOptIns.toLocaleString('pt-BR')} pessoas apagados`,
        `${result.deletedEvents.toLocaleString('pt-BR')} eventos de ligar e desligar foram junto. A funcionalidade e as respostas de pesquisa continuam como estavam.`,
      );
    },
    onError: (error: Error) => {
      /*
        Refetched on failure too, for the same reason as everywhere else in this panel: nothing is
        removed here, the screen is put back in step with the BFF. A `400` deleted nothing, so the
        numbers come back exactly as they were — the honest answer to a key that did not match.
      */
      void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.adoption(featureId) });
      toast.failure('Não foi possível apagar os dados de uso', error);
    },
  });
};
